import React from "react";
import { cn, sanitizeHTML } from "@/lib/utils";
import TextButton from "../ui/TextButton";
import { LazyMotion, domAnimation, m } from "framer-motion";
import ResponsiveImage from "../ui/ResponsiveImage";

interface ImageTextPositionProps {
  data: ImageTextPositionData;
}

interface ImageTextPositionData {
  image: any[];
  image_link: any[];
  title: string;
  text: string;
  buttonText?: string;
  buttonLink?: string;
  sectionName: string;
  boxPositioning: 1 | 2 | 3 | 4;
}

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.7,
      ease: "easeOut",
    },
  },
};

export default function ImageTextPosition({ data }: ImageTextPositionProps) {
  const sectionId = data.sectionName.replace(/\s+/g, "-");
  const cols = data.image.length + 1;
  const lgCols = `desktop:grid-cols-${cols}`;

  const useCloudinary = data.image_link.length;

  const elements = data.image.map((img, i) => {
    const useCloudinary = data.image_link[i] && data.image_link[i].item;
    const desktopImage = useCloudinary
      ? data.image_link[i].item.desktop_link
      : data.image[i].item.desktop_image;
    const tabletImage = useCloudinary
      ? data.image_link[i].item.tablet_link
      : data.image[i].item.tablet_image;
    const mobileImage = useCloudinary
      ? data.image_link[i].item.mobile_link
      : data.image[i].item.mobile_image;
    const alt = useCloudinary
      ? data.image_link[i].item.alt ?? data.title
      : data.image[i].item.imageAlt ?? data.title;

    return (
      <div key={i} className="w-full min-h-[500px] relative">
        <ResponsiveImage
          desktop_image={desktopImage}
          tablet_image={tabletImage}
          mobile_image={mobileImage}
          alt={alt}
          shouldFetch={!useCloudinary}
          fill
          className="object-cover"
          priority={false}
        />
      </div>
    );
  });

  // Prepare text box
  const textBox = (
    <div
      key="text-box"
      id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
      className="w-full flex flex-col justify-center bg-accent-color-three px-6 md:px-12 desktop:px-12 md:py-16 min-h-[500px]"
    >
      <m.h2
        className="font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-accent-color-two mb-6 desktop:mb-12"
        variants={fadeInVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {data.title}
      </m.h2>
      <m.div
        className="text-white text-paragraph-large mb-6 desktop:mb-12"
        variants={fadeInVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        dangerouslySetInnerHTML={sanitizeHTML(data.text || "")}
      />
      {data.buttonText && data.buttonLink && (
        <m.div
          variants={fadeInVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="flex justify-start"
        >
          <TextButton
            text={data.buttonText}
            link={data.buttonLink}
            textColor={"primary"}
          />
        </m.div>
      )}
    </div>
  );

  // Insert textBox at the desired position
  const layout: React.ReactNode[] = [];
  let imgIdx = 0;
  for (let i = 1; i <= cols; i++) {
    if (i === Number(data.boxPositioning)) {
      layout.push(textBox);
    } else {
      layout.push(elements[imgIdx]);
      imgIdx++;
    }
  }

  return (
    <LazyMotion features={domAnimation}>
      <div id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}>
        <section
        className={cn(
          "flex flex-col bg-accent-color-three-dark",
          `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 ${lgCols} justify-center`
        )}
      >
        {layout}
        </section>
      </div>
    </LazyMotion>
  );
}
